/* eslint-disable no-fallthrough */
'use client';

import { useRouter } from 'next/navigation';
import { useClientUserInfo } from '../../userinfo/InitialUserInfoFetcherAndParser/utils/useClientUserInfo';
import { UserTypes } from '../../userinfo/types';
import { useModalDrawerContext } from '../../components/ModalDrawerContext/ModalDrawerContext';
import { UserAccessModal } from '../../components/composableModals';
import { AccessInfoType } from './getAccessModalInfo';
import { InfoView } from '../../../portfolio/components/sections/CoolOffPeriod/InfoView';
import { RoleCheckActionType, UserAccessResult } from './utils';
import { checkPepFailedStatus } from './checkPepFailedStatus';
import { useFeatureFlag } from '../../../statsig/sdk/provider';
import { useIsBelowScreenWidth } from '../../../layouts/utils/useIsBelowScreenWidth';
import { isTimeScalarInFuture } from '@bts-web/utils-formatting';

export const useUserAccessControl = () => {
  const userData = useClientUserInfo();

  const router = useRouter();

  const { showModal, openDrawer, closeAll } = useModalDrawerContext();

  const isMobile = useIsBelowScreenWidth(1024);

  const {
    isReadOnlyUser,
    approved,
    tradingActive,
    withdrawalsActive,
    userType,
    compliance,
    trading,
  } = userData ?? {};

  const isWaitingPeriodActive = isTimeScalarInFuture(compliance?.waitTimeEnd);

  const isCoolOffActive = isTimeScalarInFuture(compliance?.coolOffTimeEnd);

  const feature_isUkLicensed = useFeatureFlag('feature_isUkLicensed');

  const coolOffNeedsAA =
    feature_isUkLicensed &&
    !isCoolOffActive &&
    compliance?.appropriateness !== 'COMPLETED';

  const checkUserAccess = (
    actionType?: RoleCheckActionType,
  ): UserAccessResult => {
    // Warning Hierarchy: business approval warning supersedes others
    if (!approved && userType === UserTypes.BUSINESS_USER) {
      return {
        allowed: false,
        accessInfoType: AccessInfoType.InfoApprovalPending,
      };
    }

    // Read-only users can only deposit
    if (isReadOnlyUser) {
      return {
        allowed: false,
        accessInfoType: AccessInfoType.ReadOnly,
      };
    }

    // Waiting period supersedes cool-off — blocks all actions
    if (isWaitingPeriodActive) {
      return {
        allowed: false,
        accessInfoType: feature_isUkLicensed
          ? AccessInfoType.UkTradeCoolOffWaiting
          : AccessInfoType.WaitingPeriodActive,
      };
    }

    // Cool-off period blocks all actions
    if (isCoolOffActive) {
      if (compliance?.appropriateness !== 'COMPLETED') {
        return {
          allowed: false,
          accessInfoType: AccessInfoType.CoolOffNeedsAA,
        };
      }

      return {
        allowed: false,
        accessInfoType: feature_isUkLicensed
          ? AccessInfoType.UkTradeCoolOffActive
          : AccessInfoType.CoolOffActive,
      };
    }

    // Post cool-off final check not yet accepted — blocks all actions
    if (trading?.reasonCode === 'USER_POST_COOLOFF_CONFIRMATION_PENDING') {
      return {
        allowed: false,
        accessInfoType: AccessInfoType.FinalCheckPending,
      };
    }

    // UK: cool-off expired but quiz not completed — redirect to quiz
    if (coolOffNeedsAA) {
      return {
        allowed: false,
        accessInfoType: AccessInfoType.CoolOffNeedsAA,
      };
    }

    if (!tradingActive && userType === UserTypes.BUSINESS_USER) {
      return {
        allowed: false,
        accessInfoType: AccessInfoType.WarningTrade,
      };
    }

    // Handle specific actions based on user permissions
    switch (actionType) {
      case RoleCheckActionType.DEPOSIT:
        // Everyone can deposit
        return { allowed: true };

      case RoleCheckActionType.WITHDRAW:
        // Check withdrawals_active for both business and retail users
        if (!withdrawalsActive) {
          return {
            allowed: false,
            accessInfoType: AccessInfoType.WarningTransfer,
          };
        }

        return { allowed: true };

      case RoleCheckActionType.TRADE:

      case RoleCheckActionType.BUY:

      case RoleCheckActionType.SELL:

      case RoleCheckActionType.SWAP:
        // Trading actions require tradingActive
        if (!tradingActive) {
          return {
            allowed: false,
            accessInfoType: AccessInfoType.WarningTrade,
          };
        }

        return { allowed: true };

      default:
        return { allowed: true };
    }
  };

  const interceptAction = (
    actionType: RoleCheckActionType,
    callback: () => void,
  ) => {
    return (event: React.MouseEvent<HTMLButtonElement>) => {
      // Safely call preventDefault and stopPropagation if they exist
      if (event?.preventDefault && typeof event.preventDefault === 'function') {
        event.preventDefault();
      }

      if (
        event?.stopPropagation &&
        typeof event.stopPropagation === 'function'
      ) {
        event.stopPropagation();
      }

      const accessResult = checkUserAccess(actionType);

      if (!accessResult.allowed && accessResult.accessInfoType) {
        if (accessResult.accessInfoType === AccessInfoType.CoolOffNeedsAA) {
          closeAll();

          router.push('/appropriateness-questions/introduction?next=portfolio');

          return;
        }

        if (
          accessResult.accessInfoType === AccessInfoType.UkTradeCoolOffActive
        ) {
          if (isMobile) {
            openDrawer(
              () => (
                <InfoView isMobile={true} mode="coolOff" isAACompleted={true} />
              ),
              { position: 'bottom' },
            );
          } else {
            showModal(({ onClose }) => (
              <InfoView
                isMobile={false}
                mode="coolOff"
                isAACompleted={true}
                onClose={onClose}
              />
            ));
          }

          return;
        }

        if (
          accessResult.accessInfoType === AccessInfoType.UkTradeCoolOffWaiting
        ) {
          if (isMobile) {
            openDrawer(
              () => <InfoView isMobile={true} mode="tradeWaitingPeriod" />,
              { position: 'bottom' },
            );
          } else {
            showModal(({ onClose }) => (
              <InfoView
                isMobile={false}
                mode="tradeWaitingPeriod"
                onClose={onClose}
              />
            ));
          }

          return;
        }

        showModal(UserAccessModal, {
          type: accessResult.accessInfoType,
        });

        return;
      }

      callback();
    };
  };

  const shouldInterceptAction = (actionType: RoleCheckActionType): boolean => {
    const accessResult = checkUserAccess(actionType);

    return !accessResult.allowed;
  };

  return {
    isPepFailed: checkPepFailedStatus(userData),
    isCoolOffActive,
    isWaitingPeriodActive,
    coolOffNeedsAA,
    checkUserAccess,
    interceptAction,
    shouldInterceptAction,
  };
};
